// ╔══════════════════════════════════════════════════════════════╗
// ║  Production Stages — list + CRUD + calendar heatmap           ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.jsx';
import { useToast } from '../../components/Toast.jsx';
import { fmtAR } from '../../lib/format.js';
import { Badge, Btn, Card, Field, Input, Modal, Select } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';

const PRESET_COLORS = [
  '#cc9c46', '#b78436', '#9a6a2c', '#7c5326', '#d8b46c',
  '#a39885', '#10b981', '#0ea5e9', '#8b5cf6', '#f43f5e',
];

export default function ProductionStagesPage() {
  const { user } = useAuth();
  const isManager = user?.role === 'admin' || user?.role === 'eng';
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [calendarStage, setCalendarStage] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['production', 'stages'],
    queryFn: () => api.get('/production/stages').then((r) => r.data),
  });

  const stages = data?.stages ?? [];

  const saveMut = useMutation({
    mutationFn: (payload) => {
      if (editing?.id && !editing.new) {
        return api.patch(`/production/stages/${editing.id}`, payload);
      }
      return api.post('/production/stages', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production', 'stages'] });
      qc.invalidateQueries({ queryKey: ['reports'] });
      toast.push({
        tone: 'success',
        title: editing?.new ? 'تم إضافة المرحلة' : 'تم حفظ التعديلات',
      });
      setEditing(null);
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل الحفظ', body: getErrorMessage(err) }),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/production/stages/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production', 'stages'] });
      toast.push({ tone: 'success', title: 'تم حذف المرحلة' });
      setDeleting(null);
    },
    onError: (err) =>
      toast.push({ tone: 'danger', title: 'فشل', body: getErrorMessage(err) }),
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            خط الإنتاج
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">
            إدارة المراحل
            <span className="text-ink-400 font-bold mr-2 tabular">· {fmtAR(stages.length)}</span>
          </h1>
        </div>
        {isManager && (
          <Btn variant="primary" icon="plus" onClick={() => setEditing({ new: true })}>
            إضافة مرحلة
          </Btn>
        )}
      </div>

      {/* Summary card for the whole production cycle */}
      {!isLoading && stages.length > 0 && <ProductionSummary stages={stages} />}

      {isLoading ? (
        <Card className="p-8 text-center text-ink-400">جاري التحميل…</Card>
      ) : stages.length === 0 ? (
        <Card className="p-12 text-center">
          <Icon name="layers" size={48} className="mx-auto text-ink-300 mb-3" />
          <p className="text-ink-500 font-bold">لا توجد مراحل</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {stages.map((s) => (
            <Card key={s.id} className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span
                    className="w-9 h-9 rounded-lg shrink-0 grid place-items-center text-white font-extrabold text-xs"
                    style={{ backgroundColor: s.color }}
                  >
                    {s.sortOrder}
                  </span>
                  <div className="min-w-0">
                    <div className="font-bold truncate">{s.nameAr}</div>
                    <div className="text-[11px] text-ink-400 font-mono truncate">
                      {s.id}
                      {s.machineType && (
                        <span> · {s.machineType}</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/production/workers?stage=${s.id}`)}
                  className="text-xs text-brand-500 font-bold hover:underline whitespace-nowrap"
                  title="عرض عمال هذه المرحلة"
                >
                  {fmtAR(s.workerCount)} عامل ←
                </button>
              </div>

              {/* Real production data */}
              {(s.timePerPieceSeconds || s.dailyTarget || s.workstations || s.workersTimeSumSeconds) && (
                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  {/* Phase I: stage time = sum of workers' times (preferred),
                      fallback to the stage's own time per piece. */}
                  {(() => {
                    const sumSec = s.workersTimeSumSeconds ?? 0;
                    const showSum = sumSec > 0;
                    const seconds = showSum ? sumSec : s.timePerPieceSeconds;
                    if (seconds == null) return null;
                    return (
                      <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                        <div className="text-ink-400">
                          {showSum ? 'زمن العمال (مجموع)' : 'الزمن/قطعة'}
                        </div>
                        <div className="font-bold tabular">
                          {fmtAR(seconds)}<span className="text-[10px] text-ink-400">ث</span>
                        </div>
                      </div>
                    );
                  })()}
                  {s.dailyTarget != null && (
                    <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                      <div className="text-ink-400">الهدف اليومي</div>
                      <div className="font-bold tabular">{fmtAR(s.dailyTarget)}</div>
                    </div>
                  )}
                  {s.workstations != null && (
                    <div className="p-2 rounded-lg bg-ink-50 dark:bg-ink-800/40">
                      <div className="text-ink-400">المحطات</div>
                      <div className="font-bold tabular">
                        {fmtAR(Math.round(Number(s.workstations)))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {s.notes && (
                <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 px-2 py-1 rounded mb-3">
                  📌 {s.notes}
                </div>
              )}

              <div className="flex gap-2 pt-3 border-t border-ink-100 dark:border-ink-800">
                <Btn
                  variant="outline"
                  size="sm"
                  icon="cal"
                  onClick={() => setCalendarStage(s)}
                >
                  تقويم الإنتاج
                </Btn>
                {isManager && (
                  <>
                    <Btn variant="ghost" size="sm" icon="edit" onClick={() => setEditing(s)}>
                      تعديل
                    </Btn>
                    <Btn
                      variant="ghost"
                      size="sm"
                      icon="trash"
                      onClick={() => setDeleting(s)}
                      disabled={s.workerCount > 0}
                      title={s.workerCount > 0 ? 'يوجد عمال — انقلهم أولاً' : ''}
                    >
                      حذف
                    </Btn>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <StageForm
          stage={editing.new ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(p) => saveMut.mutate(p)}
          loading={saveMut.isPending}
        />
      )}

      {deleting && (
        <Modal
          open
          onClose={() => setDeleting(null)}
          title="حذف مرحلة"
          size="sm"
          footer={
            <>
              <Btn variant="ghost" onClick={() => setDeleting(null)}>إلغاء</Btn>
              <Btn variant="danger" icon="trash" loading={delMut.isPending} onClick={() => delMut.mutate(deleting.id)}>
                تأكيد الحذف
              </Btn>
            </>
          }
        >
          <p className="text-sm">
            هل أنت متأكد من حذف <span className="font-bold">{deleting.nameAr}</span>؟
          </p>
          <p className="text-xs text-rose-600 mt-2">هذا الإجراء لا يمكن التراجع عنه.</p>
        </Modal>
      )}

      {calendarStage && (
        <StageCalendarModal stage={calendarStage} onClose={() => setCalendarStage(null)} />
      )}
    </div>
  );
}

// ─── Production cycle summary ───
function ProductionSummary({ stages }) {
  // Phase I: total cycle time = sum of (each stage's workers' times, or the
  // stage's own time per piece if no workers are assigned yet).
  const totalSeconds = stages.reduce((a, s) => {
    const sumSec = s.workersTimeSumSeconds ?? 0;
    return a + (sumSec > 0 ? sumSec : s.timePerPieceSeconds ?? 0);
  }, 0);
  const totalMinutes = totalSeconds / 60;
  const totalWorkstations = stages.reduce(
    (a, s) => a + Math.round(Number(s.workstations ?? 0)),
    0,
  );
  const machineCount = new Set(stages.map((s) => s.machineType).filter(Boolean)).size;

  // Group by machine type
  const byMachine = {};
  for (const s of stages) {
    if (!s.machineType) continue;
    byMachine[s.machineType] = (byMachine[s.machineType] ?? 0) + 1;
  }

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            ملخص دورة الإنتاج
          </div>
          <h3 className="font-bold text-base mt-0.5">جلابية سعودي</h3>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <SummaryCard label="عدد المراحل" value={fmtAR(stages.length)} sub="مرحلة" />
        <SummaryCard
          label="إجمالي الزمن"
          value={`${totalMinutes.toFixed(1)} د`}
          sub={`${fmtAR(totalSeconds)} ثانية للقطعة`}
          tone="brand"
        />
        <SummaryCard label="إجمالي المحطات" value={fmtAR(totalWorkstations)} sub="عامل تقديري" />
        <SummaryCard label="أنواع الماكينات" value={fmtAR(machineCount)} sub="نوع مختلف" />
      </div>

      {/* Breakdown by machine type */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(byMachine).map(([machine, count]) => (
          <span
            key={machine}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-ink-50 dark:bg-ink-800/40 text-xs"
          >
            <span className="font-bold">{machine}</span>
            <span className="text-ink-400">×{count}</span>
          </span>
        ))}
      </div>
    </Card>
  );
}

function SummaryCard({ label, value, sub, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-ink-700 dark:text-ink-100',
    brand: 'text-brand-600 dark:text-brand-300',
  };
  return (
    <div className="p-3 rounded-xl bg-ink-50 dark:bg-ink-800/40">
      <div className="text-[10px] uppercase text-ink-400 font-bold">{label}</div>
      <div className={`text-xl font-black tabular mt-0.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-ink-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Calendar Modal ───
function StageCalendarModal({ stage, onClose }) {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const { data, isLoading } = useQuery({
    queryKey: ['production', 'stages', stage.id, 'calendar', month],
    queryFn: () =>
      api
        .get(`/production/stages/${stage.id}/calendar`, { params: { month } })
        .then((r) => r.data),
  });

  const days = data?.days ?? [];
  const totals = data?.totals ?? { produced: 0, target: 0, hits: 0, misses: 0 };
  const maxProduced = Math.max(...days.map((d) => d.produced), 1);

  // Build calendar grid (starting from sunday)
  const [y, m] = month.split('-').map(Number);
  const firstDay = new Date(y, m - 1, 1);
  const firstWeekday = firstDay.getDay(); // 0=Sunday

  const calendarCells = [
    ...Array.from({ length: firstWeekday }).map(() => null),
    ...days,
  ];

  // Heatmap intensity based on production ratio
  function intensity(d) {
    if (d.produced === 0 && d.target === 0) return 0;
    if (d.target === 0) return 0.4;
    return Math.min(1, d.produced / d.target);
  }

  function dayColor(d) {
    if (d.produced === 0 && d.entries === 0) return 'transparent';
    const ratio = d.target > 0 ? d.produced / d.target : 0;
    if (ratio >= 1) return '#10b981'; // green - hit target
    if (ratio >= 0.8) return '#cc9c46'; // brand - close
    if (ratio >= 0.5) return '#f59e0b'; // amber - mid
    return '#f43f5e'; // rose - low
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`تقويم إنتاج: ${stage.nameAr}`}
      size="xl"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إغلاق</Btn>
          <Btn
            variant="primary"
            icon="users"
            onClick={() => {
              onClose();
              navigate(`/production/workers?stage=${stage.id}`);
            }}
          >
            عرض العمال
          </Btn>
        </>
      }
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Field label="الشهر">
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            dir="ltr"
            className="w-44"
          />
        </Field>
        <div className="flex-1" />
        <Badge tone="brand" dot>
          {fmtAR(data?.workerCount ?? 0)} عامل
        </Badge>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <TotalCard label="إنتاج الشهر" value={fmtAR(totals.produced)} sub="قطعة" tone="brand" />
        <TotalCard
          label="الهدف"
          value={fmtAR(totals.target)}
          sub={`${totals.target > 0 ? Math.round((totals.produced / totals.target) * 100) : 0}% تحقيق`}
        />
        <TotalCard label="أيام حقّقت الهدف" value={fmtAR(totals.hits)} tone="success" />
        <TotalCard label="أيام تحت الهدف" value={fmtAR(totals.misses)} tone="warn" />
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-ink-400">جاري التحميل…</div>
      ) : (
        <>
          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1.5 mb-1.5 text-center text-[10px] font-bold text-ink-400">
            {['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'].map(
              (w) => (
                <div key={w}>{w}</div>
              ),
            )}
          </div>

          {/* Heatmap */}
          <div className="grid grid-cols-7 gap-1.5">
            {calendarCells.map((cell, i) =>
              cell ? (
                <DayCell key={i} day={cell} color={dayColor(cell)} maxProduced={maxProduced} />
              ) : (
                <div key={i} />
              ),
            )}
          </div>

          {/* Legend */}
          <div className="mt-5 flex flex-wrap items-center gap-3 text-[11px]">
            <span className="text-ink-500 font-bold">المفتاح:</span>
            <LegendDot color="#10b981" label="حقّقوا الهدف ≥100%" />
            <LegendDot color="#cc9c46" label="80-99%" />
            <LegendDot color="#f59e0b" label="50-79%" />
            <LegendDot color="#f43f5e" label="<50%" />
            <LegendDot color="transparent" label="بدون نشاط" border />
          </div>
        </>
      )}
    </Modal>
  );
}

function DayCell({ day, color, maxProduced }) {
  const opacity = day.produced === 0 ? 0 : Math.max(0.25, day.produced / maxProduced);
  const tooltip = `${day.date}\nإنتاج: ${day.produced} / ${day.target}\nحقّق: ${day.hits} · تحت: ${day.misses}`;
  return (
    <div
      title={tooltip}
      className="aspect-square rounded-md ring-1 ring-ink-100 dark:ring-ink-800 p-1 flex flex-col justify-between relative cursor-help hover:ring-brand-400 transition"
      style={{
        backgroundColor: color === 'transparent' ? undefined : color,
        opacity: color === 'transparent' ? 1 : 0.2 + 0.8 * opacity,
      }}
    >
      <span className="text-[10px] font-bold text-ink-700 dark:text-ink-100">{day.day}</span>
      {day.produced > 0 && (
        <span className="text-[10px] font-bold tabular text-white drop-shadow">
          {fmtAR(day.produced)}
        </span>
      )}
    </div>
  );
}

function LegendDot({ color, label, border }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`w-3 h-3 rounded ${border ? 'ring-1 ring-ink-300' : ''}`}
        style={{ backgroundColor: color === 'transparent' ? undefined : color }}
      />
      <span className="text-ink-500">{label}</span>
    </span>
  );
}

function TotalCard({ label, value, sub, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-ink-700 dark:text-ink-100',
    brand: 'text-brand-600 dark:text-brand-300',
    success: 'text-emerald-600',
    warn: 'text-amber-600',
  };
  return (
    <div className="p-3 rounded-xl bg-ink-50 dark:bg-ink-800/40">
      <div className="text-[10px] uppercase text-ink-400 font-bold">{label}</div>
      <div className={`text-xl font-black tabular mt-0.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-ink-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function StageForm({ stage, onClose, onSave, loading }) {
  const isEdit = !!stage;
  const [form, setForm] = useState({
    id: stage?.id ?? '',
    nameAr: stage?.nameAr ?? '',
    nameEn: stage?.nameEn ?? '',
    color: stage?.color ?? '#cc9c46',
    unit: stage?.unit ?? 'قطعة',
    sortOrder: stage?.sortOrder ?? 99,
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function handleSubmit(e) {
    e.preventDefault();
    const payload = isEdit
      ? {
          nameAr: form.nameAr,
          nameEn: form.nameEn,
          color: form.color,
          unit: form.unit,
          sortOrder: Number(form.sortOrder),
        }
      : { ...form, sortOrder: Number(form.sortOrder) };
    onSave(payload);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `تعديل: ${stage.nameAr}` : 'إضافة مرحلة جديدة'}
      size="md"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
          <Btn type="submit" form="stage-form" variant="primary" icon="check" loading={loading}>
            {isEdit ? 'حفظ' : 'إضافة'}
          </Btn>
        </>
      }
    >
      <form id="stage-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="معرّف لاتيني" hint="حروف صغيرة فقط">
          <Input
            value={form.id}
            onChange={upd('id')}
            required
            minLength={1}
            maxLength={20}
            disabled={isEdit}
            dir="ltr"
            pattern="[a-z][a-z0-9_]*"
            placeholder="cut"
          />
        </Field>
        <Field label="الترتيب">
          <Input type="number" value={form.sortOrder} onChange={upd('sortOrder')} min={0} />
        </Field>
        <Field label="الاسم بالعربية">
          <Input value={form.nameAr} onChange={upd('nameAr')} required minLength={2} />
        </Field>
        <Field label="الاسم بالإنجليزية">
          <Input value={form.nameEn} onChange={upd('nameEn')} required minLength={2} dir="ltr" />
        </Field>
        <Field label="وحدة القياس">
          <Input value={form.unit} onChange={upd('unit')} required />
        </Field>
        <Field label="اللون">
          <div className="flex flex-wrap items-center gap-2">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setForm((f) => ({ ...f, color: c }))}
                className={`w-7 h-7 rounded-lg ring-2 transition ${
                  form.color === c ? 'ring-brand-500 scale-110' : 'ring-transparent'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
            <input
              type="color"
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
              className="w-7 h-7 rounded-lg cursor-pointer"
            />
          </div>
        </Field>
      </form>
    </Modal>
  );
}
